/**
 * DraftsTab Component
 * 
 * Display and manage draft campaigns
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Calendar, Trash2, Play } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import EmptyState from '../common/EmptyState';
import { listDrafts, deleteDraft as deleteDraftApi, launchCampaign } from '../../api/outboundApi';
import { useSettings } from '../../contexts/SettingsContext';

const DraftsTab = ({ onSwitchTab }) => {
  const { selectedLocationId } = useSettings();
  const [drafts, setDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [launchingId, setLaunchingId] = useState(null);

  const fetchDrafts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listDrafts({ locationId: selectedLocationId });
      setDrafts(response);
    } catch (error) {
      console.error('Error fetching drafts:', error);
      setDrafts([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId]);

  // Fetch drafts when location changes
  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const handleDelete = async (draftId) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) {
      return;
    }

    setDeletingId(draftId);
    try {
      await deleteDraftApi(draftId);
      setDrafts(prev => prev.filter(d => (d.itemId || d.draftId) !== draftId));
      alert('Draft deleted successfully!');
    } catch (error) {
      console.error('Error deleting draft:', error);
      alert(error.message || 'Failed to delete draft. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleLaunch = async (draft) => {
    const draftId = draft.itemId || draft.draftId;
    const cost = draft.estimatedCost || (draft.totalAppointments * 0.50);
    
    if (!window.confirm(`Launch campaign with ${draft.totalAppointments} appointments?\n\nCost: $${cost.toFixed(2)} upfront`)) {
      return;
    }

    setLaunchingId(draftId);
    try {
      const campaign = await launchCampaign({
        name: draft.name,
        locationId: draft.locationId,
        csvData: draft.csvData,
        totalAppointments: draft.totalAppointments,
        draftId: draftId
      });
      
      const campaignId = campaign.itemId || campaign.campaignId;
      alert(`Campaign launched successfully!\n\nCampaign ID: ${campaignId}\nStatus: ${campaign.status}`);
      
      // Remove from drafts
      setDrafts(prev => prev.filter(d => (d.itemId || d.draftId) !== draftId));
      
      // Switch to History tab
      if (onSwitchTab) {
        onSwitchTab('history');
      }
    } catch (error) {
      console.error('Error launching campaign:', error);
      alert(error.message || 'Failed to launch campaign. Please try again.');
    } finally {
      setLaunchingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AD0E6]"></div>
        <p className="ml-3 text-gray-400">Loading drafts...</p>
      </div>
    );
  }

  // Empty state
  if (drafts.length === 0) {
    return (
      <EmptyState
        icon={<FileText className="w-8 h-8 text-gray-400" />}
        title="No Draft Campaigns"
        description="You haven't saved any draft campaigns yet. Create a campaign and save it as a draft to see it here."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-white">
          Draft Campaigns ({drafts.length})
        </h3>
        <p className="text-sm text-gray-400">
          Review and launch your saved campaign drafts
        </p>
      </div>

      {drafts.map((draft) => {
        const draftId = draft.itemId || draft.draftId;
        return (
          <Card key={draftId} className="bg-gray-800/50 border-gray-700 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-3 bg-gray-700/50 rounded-lg">
                  <FileText className="w-6 h-6 text-[#3AD0E6]" />
                </div>
                
                <div className="flex-1">
                  <h4 className="text-lg font-medium text-white mb-3">{draft.name}</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Appointments</p>
                      <p className="text-white font-medium">{draft.totalAppointments}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Estimated Cost</p>
                      <p className="text-white font-medium">${draft.estimatedCost.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Created</p>
                      <p className="text-white font-medium flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(draft.createdAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(draftId)}
                      disabled={deletingId === draftId || launchingId === draftId}
                      className="bg-gray-700 border-gray-600 text-gray-300 hover:bg-red-900/20 hover:text-red-400 hover:border-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {deletingId === draftId ? 'Deleting...' : 'Delete'}
                    </Button>
                    
                    <Button
                      onClick={() => handleLaunch(draft)}
                      disabled={deletingId === draftId || launchingId === draftId}
                      className="bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] text-white hover:opacity-90"
                      size="sm"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {launchingId === draftId ? 'Launching...' : 'Launch Campaign'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default DraftsTab;

